import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Get the current user's authentication token
 */
async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

/**
 * Generic API fetch wrapper with timeout and error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_CONFIG.volatilityBaseUrl}${endpoint}`;

  // Check if we're using a placeholder URL (local development)
  if (API_CONFIG.volatilityBaseUrl.includes('your-api-gateway-url') ||
      API_CONFIG.volatilityBaseUrl.includes('PLACEHOLDER') ||
      API_CONFIG.volatilityBaseUrl === 'http://localhost:3000') {
    console.error('Volatility API not configured. Please set VITE_VOLATILITY_API_BASE_URL environment variable.');
    throw new Error('Volatility API endpoint not configured. Please deploy the API or set VITE_VOLATILITY_API_BASE_URL.');
  }

  // Get authentication token
  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Volatility metrics response from the API
 */
export interface VolatilityMetrics {
  product_id: string;
  granularity_mins: number;
  lookback_days: number;
  current_atr: number;
  average_atr: number | null;  // Can be null when no historical data exists
  volatility_percentile: number;  // 0-100 scale
  volatility_regime: 'LOW' | 'NORMAL' | 'HIGH' | 'EXTREME';  // Changed from current_regime to match API
  atr_timestamp: string;  // ISO timestamp - changed from metric_time to match API
  sample_count: number;
  position_multiplier?: number;  // Calculated based on regime
}

/**
 * Volatility status for a specific trade group
 */
export interface VolatilityStatus {
  trade_group_id: string;
  // Configuration
  volatility_period: string;
  initial_deployment_pct: number;
  low_volatility_threshold: number;
  high_volatility_threshold: number;
  extreme_volatility_threshold: number;
  low_volatility_position_multiplier: number;
  high_volatility_position_multiplier: number;
  rebalance_sensitivity: number;
  min_rebalance_interval_hours: number;
  // State
  current_atr: number;
  average_atr: number;
  volatility_percentile: number;
  previous_volatility_percentile: number;
  current_volatility_regime: 'LOW' | 'NORMAL' | 'HIGH' | 'EXTREME';
  last_volatility_check: string | null;
  last_rebalance_time: string | null;
  capital_deployed_pct: number;
  candle_count: number;
}

/**
 * Get current volatility metrics for a product
 * @param productId - The product ID (e.g., 'BTC-USDC')
 * @param granularity - Optional granularity in minutes (default: 60)
 * @param lookback - Optional lookback period in days (default: 7)
 */
export async function getVolatilityMetrics(
  productId: string,
  granularity: number = 60,
  lookback: number = 7
): Promise<VolatilityMetrics> {
  const queryParams = new URLSearchParams({
    granularity: granularity.toString(),
    lookback: lookback.toString(),
  });

  const metrics = await apiFetch<VolatilityMetrics>(
    `/v1/volatility-metrics/${productId}?${queryParams}`,
    { method: 'GET' }
  );

  // Calculate position multiplier based on regime
  let positionMultiplier = 1.0;
  if (metrics.volatility_regime === 'LOW') {
    positionMultiplier = 1.25;  // 25% larger positions
  } else if (metrics.volatility_regime === 'HIGH') {
    positionMultiplier = 0.50;  // 50% smaller positions
  } else if (metrics.volatility_regime === 'EXTREME') {
    positionMultiplier = 0.25;  // 75% smaller positions
  }

  return {
    ...metrics,
    position_multiplier: positionMultiplier,
  };
}

/**
 * Get volatility status for a specific trade group
 * @param tradeGroupId - The trade group ID
 */
export async function getVolatilityStatus(
  tradeGroupId: string
): Promise<VolatilityStatus> {
  return apiFetch<VolatilityStatus>(
    `/v1/trade-groups/${tradeGroupId}/volatility-status`,
    { method: 'GET' }
  );
}

/**
 * Get volatility metrics for multiple products
 * @param productIds - Array of product IDs
 * @param granularity - Optional granularity in minutes
 * @param lookback - Optional lookback period in days
 */
export async function getMultipleVolatilityMetrics(
  productIds: string[],
  granularity: number = 60,
  lookback: number = 7
): Promise<VolatilityMetrics[]> {
  return Promise.all(
    productIds.map(productId => getVolatilityMetrics(productId, granularity, lookback))
  );
}
